"use client";

import React from "react";
import { Loader, CircleCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const BRAND_OPTIONS = ["Weddings & Engagements", "Private Dining", "Workshops & Creative", "Corporate Retreats", "Birthday Celebrations"];
function DropAlertsForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="122664" className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in duration-500">
        <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-2">
          <CircleCheck className="h-8 w-8 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold tracking-tight text-primary font-serif">You're Subscribed</h3>
          <p className="text-muted-foreground max-w-sm mx-auto">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-8 rounded-lg border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground transition-colors uppercase tracking-wider text-xs font-bold h-12 px-8">
          Sign up another email
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-5">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-sans">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Your name" className="h-12 rounded-lg border-input bg-background/50 focus-visible:border-primary focus-visible:ring-0 transition-colors" {...field} value={field.value || ''} />
              </FormControl>
              <FormDescription className="text-[10px] uppercase tracking-wider opacity-50">Optional – helps us personalize your updates.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-sans">Email Address *</FormLabel>
              <FormControl>
                <Input type="email" placeholder="hello@example.com" className="h-12 rounded-lg border-input bg-background/50 focus-visible:border-primary focus-visible:ring-0 transition-colors" {...field} value={field.value || ''} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^\+?[0-9\s\-\(\)]+$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-sans">WhatsApp Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+62 ..." className="h-12 rounded-lg border-input bg-background/50 focus-visible:border-primary focus-visible:ring-0 transition-colors" {...field} value={field.value || ''} />
              </FormControl>
              <FormDescription className="text-[10px] uppercase tracking-wider opacity-50">Optional – for direct event coordination.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="brand_preferences" render={() => <FormItem className="pt-4 border-t border-border mt-6">
              <div className="mb-4">
                <FormLabel className="text-xs uppercase tracking-wider text-primary font-sans">Interested Events</FormLabel>
                <FormDescription className="text-[10px] uppercase tracking-wider opacity-50 mt-1">Select the types of gatherings you're planning.</FormDescription>
              </div>
              <div className="grid grid-cols-2 gap-y-3 gap-x-4">
                {BRAND_OPTIONS.map((brand, i) => <div key={brand} data-index={i}>
                    <FormField control={control} name="brand_preferences" render={({
              field
            }) => {
              const currentValue = Array.isArray(field.value) ? field.value : [];
              return <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                            <FormControl>
                              <div className="relative flex items-center justify-center">
                                <input type="checkbox" className="peer h-4 w-4 appearance-none rounded-md border border-input bg-transparent checked:bg-primary checked:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 transition-all cursor-pointer" checked={currentValue.includes(brand)} onChange={e => {
                      const updated = e.target.checked ? [...currentValue, brand] : currentValue.filter((val: string) => val !== brand);
                      field.onChange(updated);
                    }} />
                                <CircleCheck className="absolute h-3 w-3 text-primary-foreground opacity-0 peer-checked:opacity-100 pointer-events-none" strokeWidth={3} />
                              </div>
                            </FormControl>
                            <FormLabel className="font-normal text-sm text-muted-foreground cursor-pointer hover:text-primary transition-colors font-sans">
                              {brand}
                            </FormLabel>
                          </FormItem>;
            }} />
                  </div>)}
              </div>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="consent" rules={{
        required: "You must agree to receive notifications to subscribe."
      }} render={({
        field
      }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-6 border-t border-border mt-6">
              <FormControl>
                <div className="relative flex items-center justify-center mt-1">
                  <input type="checkbox" className="peer h-4 w-4 appearance-none rounded-md border border-input bg-transparent checked:bg-primary checked:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 transition-all cursor-pointer" checked={!!field.value} onChange={field.onChange} />
                  <CircleCheck className="absolute h-3 w-3 text-primary-foreground opacity-0 peer-checked:opacity-100 pointer-events-none" strokeWidth={3} />
                </div>
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-xs text-muted-foreground font-normal leading-relaxed cursor-pointer hover:text-primary transition-colors font-sans">
                  I agree to receive event planning tips and venue updates via email. *
                </FormLabel>
                <FormMessage />
              </div>
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-lg">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 mt-8 bg-primary text-primary-foreground hover:opacity-90 rounded-lg text-sm font-bold tracking-widest uppercase transition-all shadow-md">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 h-5 w-5" />
            Joining...
          </> : 'Subscribe to Tips'}
      </Button>
    </Form>;
}
export default function DropAlerts() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="stay-updated" className="bg-background text-foreground py-24 px-4 md:px-8 flex justify-center items-center min-h-[80vh]">
      <div ref={ref} className={`w-full max-w-xl bg-card/60 backdrop-blur-sm p-8 md:p-12 border border-border shadow-xl rounded-xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center mb-10 space-y-3">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-primary uppercase font-serif">
            Stay Inspired
          </h2>
          <p className="text-muted-foreground text-sm uppercase tracking-widest font-sans">
            Get event planning tips from Ubud
          </p>
        </div>

        <FormProvider formKey="stay_updated_form_journal" formId="stay_updated_form_journal" sectionName="Stay Updated" defaultValues={{
        name: "",
        email: "",
        phone: "",
        brand_preferences: [],
        consent: false
      }} validationRules={{
        email: {
          required: true,
          pattern: "email"
        },
        consent: {
          required: true
        }
      }} submitText="Subscribe to Tips" successMessage="We'll send you the latest event planning tips and venue updates from Ubud.">
          <DropAlertsForm />
        </FormProvider>
      </div>
    </section>;
}